import moment from "moment";

const THIN = { style: "thin", color: { argb: "FFD1D5DB" } };
const SEP = { style: "medium", color: { argb: "FF9CA3AF" } };

const formatDateTime = (v) =>
    v ? moment(v).format("DD-MM-YYYY, hh:mm:ss A") : "---";
const yes_no = (v) => (v ? "Yes" : "No");
const scalpingProcessed = (t) =>
    t?.isAlreadyDeducted ?? t?.isRBScalpingDeducted;

const displayVal = (v) => (v === null || v === undefined ? "---" : v);

/** Excel column width (character units): fit header on one line; respect data minimums. */
function columnWidthsForHeaders(headers, dataMinWidths) {
    const maxCap = 55;
    return headers.map((label, i) => {
        const fromTitle = Math.ceil(label.length * 1.35) + 4;
        const w = Math.max(dataMinWidths[i] ?? 12, fromTitle);
        return { width: Math.min(maxCap, w) };
    });
}

function setActionCell(cell, action) {
    const v = displayVal(action);
    cell.value = v;
    if (action === "Buy") cell.font = { color: { argb: "FF2563EB" }, bold: true };
    else if (action === "Sell")
        cell.font = { color: { argb: "FFDC2626" }, bold: true };
}

function setPlCell(cell, v) {
    cell.value = displayVal(v);
    const n = Number(v);
    if (!Number.isNaN(n)) {
        if (n < 0) cell.font = { color: { argb: "FFDC2626" }, bold: true };
        else if (n > 0) cell.font = { color: { argb: "FF2563EB" }, bold: true };
    }
}

/**
 * RB report .xlsx: each API pair = 2 rows; column A merged (No.);
 * reads from the API payload.
 */
export async function downloadRbReportGroupedExcel(
    data,
    baseFileName = "RbReport"
) {
    const details = data?.rvTradeDetails ?? [];
    if (!details.length) {
        return false;
    }

    const ExcelJS = (await import("exceljs")).default;
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("RB Report", {
        views: [{ showGridLines: false }],
    });

    const headers = [
        "No.",
        "Buy/Sell",
        "Currency Pair",
        "Quantity/Notional",
        "Entry Price",
        "Exit Price",
        "Entry Time",
        "Exit Time",
        "P&L",
        "Position ID",
        "Closed Count",
        "Scalping Process",
        "Comment",
    ];

    const headerRow = ws.addRow(headers);
    headerRow.height = 24;
    headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF9FAFB" },
        };
        cell.border = {
            top: THIN,
            bottom: THIN,
            left: THIN,
            right: THIN,
        };
        cell.alignment = {
            vertical: "middle",
            horizontal: "left",
            wrapText: false,
            shrinkToFit: false,
        };
    });

    let rowStart = 2;

    details.forEach((item, groupIdx) => {
        const f = item?.firstTrade;
        const s = item?.secondTrade;
        const isLastGroup = groupIdx === details.length - 1;
        const r1 = rowStart;
        const r2 = rowStart + 1;

        ws.mergeCells(r1, 1, r2, 1);
        const noCell = ws.getCell(r1, 1);
        noCell.value = groupIdx + 1;
        noCell.alignment = { vertical: "middle", horizontal: "left" };
        noCell.border = {
            top: groupIdx === 0 ? THIN : undefined,
            left: THIN,
            right: THIN,
            bottom: isLastGroup ? THIN : SEP,
        };

        const writePairRow = (rowIndex, trade, isFirstInPair) => {
            const top = isFirstInPair ? (groupIdx === 0 ? THIN : undefined) : undefined;
            const bottom = isFirstInPair
                ? undefined
                : isLastGroup
                  ? THIN
                  : SEP;

            for (let col = 2; col <= 13; col++) {
                const cell = ws.getRow(rowIndex).getCell(col);
                cell.border = {
                    top,
                    bottom,
                    left: THIN,
                    right: THIN,
                };
                cell.alignment = {
                    vertical: "middle",
                    horizontal: "left",
                    wrapText: col === 13,
                    shrinkToFit: false,
                };
            }

            const row = ws.getRow(rowIndex);
            setActionCell(row.getCell(2), trade?.action);
            row.getCell(3).value = displayVal(trade?.symbol);
            row.getCell(4).value = displayVal(trade?.totalCloseVolume);
            row.getCell(5).value = displayVal(trade?.openPrice);
            row.getCell(6).value = displayVal(trade?.closePrice);
            row.getCell(7).value = formatDateTime(trade?.openTime);
            row.getCell(8).value = formatDateTime(trade?.closeTime);
            setPlCell(row.getCell(9), trade?.netProfit);
            row.getCell(10).value = displayVal(trade?.positionId);
            row.getCell(11).value = displayVal(trade?.closeDealsCount);
            row.getCell(12).value = yes_no(scalpingProcessed(trade));
            row.getCell(13).value = displayVal(trade?.message);
        };

        writePairRow(r1, f, true);
        writePairRow(r2, s, false);

        ws.getRow(r1).height = 20;
        ws.getRow(r2).height = 20;

        rowStart += 2;
    });

    const dataMinWidths = [
        6, 11, 14, 22, 13, 13, 24, 24, 11, 14, 13, 22, 32,
    ];
    ws.columns = columnWidthsForHeaders(headers, dataMinWidths);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${baseFileName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
}
